/** Bound the whole logical operation, including response-body reads. */
export const REQUEST_TIMEOUT_MS = 15_000;

export class RequestDeadlineError extends Error {
  constructor() {
    super('Request deadline exceeded');
    this.name = 'RequestDeadlineError';
  }
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('The operation was aborted', 'AbortError');
  }
}

export async function withRequestDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  { signal, timeoutMs = REQUEST_TIMEOUT_MS }: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<T> {
  if (!Number.isInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 120_000) {
    throw new RangeError('Invalid request timeout');
  }
  throwIfAborted(signal);
  const controller = new AbortController();
  let closed = false;
  let rejectWait!: (reason: unknown) => void;
  const cancelled = new Promise<never>((_, reject) => { rejectWait = reject; });
  const cancel = (reason: unknown) => {
    if (closed || controller.signal.aborted) return;
    rejectWait(reason);
    controller.abort(reason);
  };
  const onAbort = () => cancel(signal?.reason ?? new DOMException('The operation was aborted', 'AbortError'));
  signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => cancel(new RequestDeadlineError()), timeoutMs);
  if (signal?.aborted) onAbort();
  try {
    const running = Promise.resolve().then(() => {
      throwIfAborted(controller.signal);
      return operation(controller.signal);
    });
    return await Promise.race([running, cancelled]);
  } finally {
    closed = true;
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}
